import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useTree } from '../../api/tree';
import { useCreatePerson, useUpdatePerson, useDeletePerson } from '../../api/person';
import type { TreeNode, Person, CreatePersonDto } from '../../types/api';
import { ZoomIn, ZoomOut, Maximize2, Plus } from 'lucide-react';
import { PersonModal } from '../person/PersonModal';
import { PersonForm } from '../person/PersonForm';
import { DeleteConfirmDialog } from '../person/DeleteConfirmDialog';
import { useToast } from '../ui/Toast';
import { LoadingSpinner, ErrorState } from '../ui/States';

export const TreeView = () => {
    const svgRef = useRef<SVGSVGElement>(null);
    const { data: treeData, isLoading, error } = useTree('nested');
    const [zoomLevel, setZoomLevel] = useState(1);
    const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

    // Modal states
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [isPersonModalOpen, setIsPersonModalOpen] = useState(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [personToEdit, setPersonToEdit] = useState<Person | null>(null);
    const [personToDelete, setPersonToDelete] = useState<Person | null>(null);

    // Expand/collapse state
    const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

    // Mutations & toast
    const createPerson = useCreatePerson();
    const updatePerson = useUpdatePerson();
    const deletePerson = useDeletePerson();
    const { showToast } = useToast();

    const toggleCollapse = useCallback((nodeId: string) => {
        setCollapsedNodes(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    }, []);

    useEffect(() => {
        if (!treeData || !svgRef.current || !Array.isArray(treeData) || treeData.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = 1400;
        const height = 800;
        const margin = { top: 60, right: 160, bottom: 60, left: 160 };

        const zoom = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.1, 3])
            .on('zoom', (event) => {
                g.attr('transform', event.transform.toString());
                setZoomLevel(event.transform.k);
            });

        zoomRef.current = zoom;
        svg.call(zoom as any);

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Build hierarchy and apply collapsed state
        const root = d3.hierarchy<TreeNode>(treeData[0]);

        // Collapse children of collapsed nodes
        root.each((d: any) => {
            if (collapsedNodes.has(d.data.id) && d.children) {
                d._children = d.children;
                d.children = null;
            }
        });

        const treeLayout = d3.tree<TreeNode>()
            .size([height - margin.top - margin.bottom, width - margin.left - margin.right])
            .separation((a, b) => a.parent === b.parent ? 1.2 : 1.8);

        treeLayout(root);

        // Draw curved links
        g.selectAll('.link')
            .data(root.links())
            .enter()
            .append('path')
            .attr('class', 'link')
            .attr('d', d3.linkHorizontal<any, any>()
                .x(d => d.y)
                .y(d => d.x)
            )
            .attr('fill', 'none')
            .attr('stroke', '#cbd5e1')
            .attr('stroke-width', 2)
            .attr('opacity', 0)
            .transition()
            .duration(500)
            .attr('opacity', 1);

        // Draw nodes
        const node = g.selectAll('.node')
            .data(root.descendants())
            .enter()
            .append('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${d.y},${d.x})`)
            .attr('opacity', 0);

        // Animate nodes in
        node.transition()
            .duration(500)
            .delay((_, i) => i * 30)
            .attr('opacity', 1);

        // Node background card
        node.append('rect')
            .attr('x', -55)
            .attr('y', -22)
            .attr('width', 110)
            .attr('height', 44)
            .attr('rx', 10)
            .attr('ry', 10)
            .attr('fill', d => {
                if (d.data.deathDate) return '#f1f5f9';
                return d.data.gender === 'MALE' ? '#eff6ff' : '#fdf2f8';
            })
            .attr('stroke', d => {
                if (d.data.deathDate) return '#94a3b8';
                return d.data.gender === 'MALE' ? '#93c5fd' : '#f9a8d4';
            })
            .attr('stroke-width', 1.5)
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                event.stopPropagation();
                setSelectedPerson(d.data as unknown as Person);
                setIsPersonModalOpen(true);
            })
            .on('mouseover', function () {
                d3.select(this)
                    .transition()
                    .duration(150)
                    .attr('stroke-width', 2.5)
                    .attr('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');
            })
            .on('mouseout', function (_, d) {
                d3.select(this)
                    .transition()
                    .duration(150)
                    .attr('stroke-width', 1.5)
                    .attr('filter', 'none');
            });

        // Gender indicator dot
        node.append('circle')
            .attr('cx', -42)
            .attr('cy', 0)
            .attr('r', 5)
            .attr('fill', d => {
                if (d.data.deathDate) return '#94a3b8';
                return d.data.gender === 'MALE' ? '#3b82f6' : '#ec4899';
            });

        // Name text
        node.append('text')
            .attr('x', -30)
            .attr('y', -4)
            .text(d => d.data.givenName || d.data.fullName.split(' ').pop() || '')
            .style('font-size', '12px')
            .style('fill', '#1e293b')
            .style('font-weight', '600')
            .style('pointer-events', 'none');

        // Year text
        node.append('text')
            .attr('x', -30)
            .attr('y', 12)
            .text(d => {
                const birth = d.data.birthDate ? new Date(d.data.birthDate).getFullYear() : '?';
                const death = d.data.deathDate ? new Date(d.data.deathDate).getFullYear() : '';
                return death ? `${birth}–${death}` : `${birth}`;
            })
            .style('font-size', '10px')
            .style('fill', '#64748b')
            .style('pointer-events', 'none');

        // Expand/collapse toggle button for nodes with children
        const nodesWithChildren = node.filter((d: any) => d.children || d._children);

        nodesWithChildren.append('circle')
            .attr('cx', 60)
            .attr('cy', 0)
            .attr('r', 9)
            .attr('fill', '#f8fafc')
            .attr('stroke', '#cbd5e1')
            .attr('stroke-width', 1.5)
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                event.stopPropagation();
                toggleCollapse(d.data.id);
            });

        nodesWithChildren.append('text')
            .attr('x', 60)
            .attr('y', 4)
            .attr('text-anchor', 'middle')
            .text((d: any) => d._children ? '+' : '−')
            .style('font-size', '14px')
            .style('font-weight', 'bold')
            .style('fill', '#64748b')
            .style('pointer-events', 'none');

    }, [treeData, collapsedNodes, toggleCollapse]);

    const handleZoomIn = () => {
        if (svgRef.current && zoomRef.current) {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(zoomRef.current.scaleBy as any, 1.3);
        }
    };

    const handleZoomOut = () => {
        if (svgRef.current && zoomRef.current) {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(zoomRef.current.scaleBy as any, 0.7);
        }
    };

    const handleReset = () => {
        if (svgRef.current && zoomRef.current) {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(zoomRef.current.transform as any, d3.zoomIdentity);
        }
    };

    const handleEdit = (person: Person) => {
        setPersonToEdit(person);
        setIsPersonModalOpen(false);
        setIsFormModalOpen(true);
    };

    const handleDelete = (personId: string) => {
        const person = selectedPerson;
        if (person) {
            setPersonToDelete(person);
            setIsPersonModalOpen(false);
            setIsDeleteDialogOpen(true);
        }
    };

    const handleFormSubmit = (data: CreatePersonDto) => {
        if (personToEdit) {
            updatePerson.mutate({ id: personToEdit.id, dto: data }, {
                onSuccess: () => {
                    setIsFormModalOpen(false);
                    setPersonToEdit(null);
                    showToast('Đã cập nhật thành công', 'success');
                },
                onError: () => showToast('Lỗi khi cập nhật', 'error'),
            });
        } else {
            createPerson.mutate(data, {
                onSuccess: () => {
                    setIsFormModalOpen(false);
                    showToast('Đã thêm người mới', 'success');
                },
                onError: () => showToast('Lỗi khi thêm người', 'error'),
            });
        }
    };

    const handleConfirmDelete = () => {
        if (personToDelete) {
            deletePerson.mutate(personToDelete.id, {
                onSuccess: () => {
                    setIsDeleteDialogOpen(false);
                    setPersonToDelete(null);
                    showToast('Đã xóa thành công', 'success');
                },
                onError: () => showToast('Lỗi khi xóa. Hãy xóa con cháu trước.', 'error'),
            });
        }
    };

    if (isLoading) {
        return <LoadingSpinner message="Đang tải cây gia phả..." size="lg" />;
    }

    if (error) {
        return <ErrorState message="Lỗi khi tải dữ liệu. Vui lòng kiểm tra kết nối." onRetry={() => window.location.reload()} />;
    }

    return (
        <>
            <div className="relative">
                {/* Controls */}
                <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                    <button
                        onClick={() => {
                            setPersonToEdit(null);
                            setIsFormModalOpen(true);
                        }}
                        className="p-2.5 bg-primary-600 text-white rounded-lg shadow-md hover:bg-primary-700 transition-colors"
                        title="Thêm người mới"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleZoomIn}
                        className="p-2.5 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                        title="Phóng to"
                    >
                        <ZoomIn className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                        onClick={handleZoomOut}
                        className="p-2.5 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                        title="Thu nhỏ"
                    >
                        <ZoomOut className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                        onClick={handleReset}
                        className="p-2.5 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                        title="Đặt lại"
                    >
                        <Maximize2 className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Zoom level */}
                <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-white rounded-lg shadow-md text-sm font-medium text-gray-600">
                    {Math.round(zoomLevel * 100)}%
                </div>

                {/* SVG Canvas */}
                <svg
                    ref={svgRef}
                    width="100%"
                    height="800"
                    className="border border-gray-200 rounded-xl bg-gradient-to-br from-white to-gray-50"
                />

                {/* Legend */}
                <div className="mt-4 flex gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span>Nam</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                        <span>Nữ</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                        <span>Đã mất</span>
                    </div>
                    <div className="flex items-center gap-2 ml-4 text-xs text-gray-400">
                        <span className="inline-flex items-center justify-center w-5 h-5 border border-gray-300 rounded-full text-[10px]">−</span>
                        <span>Thu gọn/mở rộng nhánh</span>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <PersonModal
                isOpen={isPersonModalOpen}
                onClose={() => setIsPersonModalOpen(false)}
                person={selectedPerson}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            <PersonForm
                isOpen={isFormModalOpen}
                onClose={() => {
                    setIsFormModalOpen(false);
                    setPersonToEdit(null);
                }}
                onSubmit={handleFormSubmit}
                person={personToEdit}
                isLoading={createPerson.isPending || updatePerson.isPending}
            />

            <DeleteConfirmDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setPersonToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                personName={personToDelete?.fullName || ''}
                isLoading={deletePerson.isPending}
            />
        </>
    );
};
